/**
 * GstRemittanceWidget (R1)
 * Shows estimated GST to collect for the current remittance period
 * and the next remittance deadline.
 *
 * Estimation only — not an accounting system.
 * Settings: /settings → Tax (gst_remittance_period, gst_reminder_days)
 */
import { useGstSummary, GstRemittanceRecord } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Receipt, AlertTriangle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { formatDateOnly } from '@/lib/utils';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
}

// next_remittance_date, period_start and period_end are all DATE columns.
// Parsing them with the bare Date constructor treats them as UTC midnight,
// which renders one calendar day early west of UTC (e.g. America/Edmonton).
// formatDateOnly parses the y/m/d components directly so the displayed date
// always matches the stored calendar date.
function formatDate(isoDate: string): string {
  return formatDateOnly(isoDate, { year: 'numeric', month: 'short', day: 'numeric' });
}

function periodLabel(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${formatDateOnly(start, opts)} – ${formatDateOnly(end, { ...opts, year: 'numeric' })}`;
}

export function GstRemittanceWidget() {
  const { data, isLoading, error } = useGstSummary();
  const [showHistory, setShowHistory] = useState(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-5 w-5" />
            GST Remittance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 animate-pulse">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-40" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-5 w-5" />
            GST Remittance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load GST data</p>
        </CardContent>
      </Card>
    );
  }

  const isUnconfigured = data.gst_estimated === 0 && data.invoice_count === 0;

  return (
    <Card data-testid="gst-remittance-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-5 w-5" />
            GST Remittance
          </CardTitle>
          {data.reminder_active && (
            <Badge variant="destructive" className="flex items-center gap-1 text-xs">
              <AlertTriangle className="h-3 w-3" />
              Due Soon
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Estimated amount */}
        <div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(data.gst_estimated)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Estimated GST to collect &bull; {data.invoice_count} invoice{data.invoice_count !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Period */}
        <div className="text-xs text-gray-600">
          <span className="font-medium">Period:</span>{' '}
          {periodLabel(data.period_start, data.period_end)}
        </div>

        {/* Next remittance */}
        <div
          className={`flex items-center gap-1 text-xs font-medium rounded px-2 py-1 ${
            data.reminder_active
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-gray-50 text-gray-700'
          }`}
        >
          {data.reminder_active && <AlertTriangle className="h-3 w-3 flex-shrink-0" />}
          Due {formatDate(data.next_remittance_date)}
          {data.days_until_remittance > 0
            ? ` (${data.days_until_remittance}d)`
            : ' (today)'}
        </div>

        {/* History toggle */}
        {data.history.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${showHistory ? 'rotate-180' : ''}`}
              />
              {showHistory ? 'Hide history' : `View history (${data.history.length})`}
            </button>

            {showHistory && (
              <div className="mt-2 space-y-1 border-t pt-2 max-h-40 overflow-y-auto">
                {data.history.map((record: GstRemittanceRecord) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0"
                  >
                    <span className="text-gray-600">
                      {periodLabel(record.period_start, record.period_end)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{formatCurrency(record.gst_collected)}</span>
                      <Badge
                        variant={record.status === 'remitted' ? 'default' : 'secondary'}
                        className="text-xs py-0"
                      >
                        {record.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isUnconfigured && (
          <p className="text-xs text-muted-foreground italic">
            Configure remittance period in Settings → Financial
          </p>
        )}
      </CardContent>
    </Card>
  );
}
